export type ClientCounterMetric = {
  type: 'counter';
  name: string;
  labels?: Record<string, string>;
};

export type ClientHistogramMetric = {
  type: 'histogram';
  name: string;
  value: number;
  labels?: Record<string, string>;
};

export type ClientMetric = ClientCounterMetric | ClientHistogramMetric;

export const CLIENT_METRIC_LABEL_NAMES: Record<string, string[]> = {
  fotoscapes_player_ad_requested: ['content_domain', 'ad_tag', 'utm_source', 'ad_unit_id'],
  fotoscapes_player_ad_rendered: ['content_domain', 'ad_tag', 'utm_source', 'ad_unit_id'],
  fotoscapes_player_ad_impression: ['content_domain', 'ad_tag', 'utm_source', 'ad_unit_id'],
  fotoscapes_player_ad_not_rendered: ['content_domain', 'ad_tag', 'utm_source', 'ad_unit_id'],
};

export function normalizeLabelValues(
  labels: Record<string, string | number | undefined> = {}
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(labels).map(([key, value]) => [key, value?.toString() ?? ''])
  );
}

export function resolveClientMetricLabelNames(
  name: string,
  labels?: Record<string, string | number | undefined>
): string[] {
  const knownLabels = CLIENT_METRIC_LABEL_NAMES[name];
  if (knownLabels) {
    return knownLabels;
  }

  return Object.keys(labels ?? {});
}

export function normalizeClientMetricLabels(
  name: string,
  labels?: Record<string, string | number | undefined>
): Record<string, string> {
  const labelNames = resolveClientMetricLabelNames(name, labels);
  const normalizedLabels = normalizeLabelValues(labels);

  return Object.fromEntries(
    labelNames.map((labelName) => [labelName, normalizedLabels[labelName] ?? ''])
  );
}

export function normalizeBatchMetrics(metrics: ClientMetric[]): ClientMetric[] {
  const labelKeysByMetricName = new Map<string, Set<string>>();

  for (const metric of metrics) {
    const keys = labelKeysByMetricName.get(metric.name) ?? new Set<string>();
    for (const key of Object.keys(metric.labels ?? {})) {
      keys.add(key);
    }
    for (const key of CLIENT_METRIC_LABEL_NAMES[metric.name] ?? []) {
      keys.add(key);
    }
    labelKeysByMetricName.set(metric.name, keys);
  }

  return metrics.map((metric) => {
    const allKeys = labelKeysByMetricName.get(metric.name);
    if (!allKeys || allKeys.size === 0) {
      return metric;
    }

    const normalizedLabels = normalizeLabelValues(metric.labels);
    const labels = Object.fromEntries(
      [...allKeys].map((key) => [key, normalizedLabels[key] ?? ''])
    );

    return { ...metric, labels };
  });
}
